import ChargeHelper from 'features/Charge/chargeHelper';
import imgFileArrowUp from 'assets/images/Dialogs/img_file-arrow-up.png';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import * as ExcelJS from 'exceljs';

DialogUploadIdentifierView.propTypes = {
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

const ALLOWED_EXTENSIONS = ['.xls', '.xlsx', '.xlsm'];

const isValidExcelFile = (file) => {
  if (!file) return false;
  const name = file.name?.toLowerCase() ?? '';
  return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

function DialogUploadIdentifierView({ onCancel, onDone }) {
  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    ChargeHelper.API.requestGetChargeIdentityPartnerList({
      page: 0,
      limit: 1000,
    }).then((response) => {
      setPartnerList(response?.partners ?? []);
    });
  }, []);

  const selectedPartnerName = _.find(
    partnerList,
    (partner) => partner.vehicleIdentityPartnerId?.toString() === selectedPartnerId?.toString()
  )?.name;

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!isValidExcelFile(file)) {
      ToastHelper.showError('Định dạng file không hợp lệ. Chỉ chấp nhận .xls, .xlsx, .xlsm');
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDownloadTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'System';
  workbook.views = [
    {
      x: 0,
      y: 0,
      width: 10000,
      height: 20000,
    },
  ];
  const sheet = workbook.addWorksheet('Danh sách mã định danh');

  sheet.columns = [
    { width: 8 },  
    { width: 40 }, 
    { width: 35 }, 
    { width: 30 }, 
  ];

  sheet.mergeCells('A1:D1');

  const titleCell = sheet.getCell('A1');
  titleCell.value = 'FILE MẪU TẢI LÊN DANH SÁCH MÃ ĐỊNH DANH (MAC/EVCC)';
  titleCell.font = {
    name: 'Times New Roman',
    bold: true,
    size: 16,
    color: { argb: 'FFFFFFFF' },
  };

  titleCell.alignment = {
    vertical: 'middle',
    horizontal: 'left',
  };

  titleCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: '2F5597' },
  };

  sheet.getRow(1).height = 32;

  const guideRows = [
    ['Hướng dẫn:'],
    ['- Điền dữ liệu mã định danh MAC/EVCC cần tạo trên hệ thống vào các cột tương ứng trên file này'],
    ['- Các cột có dấu (*) là những cột bắt buộc'],
    ['- Mã định danh là chuỗi ký tự gồm chữ cái in hoa (A–F) và số (0–9), không chứa khoảng trắng hoặc ký tự đặc biệt. Ví dụ: 141FBA10F5B4'],
    ['- Với cột "Biển số", chỉ điền các biển số đã được khai báo (đã tồn tại) trên hệ thống'],
  ];

  guideRows.forEach((row, index) => {
    const rowIndex = index + 2;

    sheet.mergeCells(`A${rowIndex}:D${rowIndex}`);

    const cell = sheet.getCell(`A${rowIndex}`);
    cell.value = row[0];

    cell.font = {
      name: 'Times New Roman',
      bold: index === 0,
      size: 14,
      color: {
        argb: index === 0 ? 'C00000' : '000000',
      },
    };

    cell.alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true,
    };

    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'F4CCCC' },
    };

    sheet.getRow(rowIndex).height = 24;
  });

  sheet.getRow(7).height = 10;

  const headerRowIndex = 8;

  const headers = [
    'STT',
    'Mã MAC/EVCC (*)',
    'Trạng thái sạc (*)',
    'Biển số xe',
  ];

  headers.forEach((header, index) => {
    const cell = sheet.getCell(headerRowIndex, index + 1);

    const isRequiredColumn =
      header === 'Mã MAC/EVCC (*)' ||
      header === 'Trạng thái sạc (*)';

    if (isRequiredColumn) {
      const normalText = header.replace(' (*)', '');

      cell.value = {
        richText: [
          {
            text: `${normalText} `,
            font: {
              name: 'Times New Roman',
              family: 4,
              size: 13,
              bold: true,
              color: { argb: '000000' },
            },
          },
          {
            text: '(*)',
            font: {
              name: 'Times New Roman',
              family: 4,
              size: 13,
              bold: true,
              color: { argb: 'FF0000' },
            },
          },
        ],
      };
    } else {
      cell.value = header;

      cell.font = {
        name: 'Times New Roman',
        family: 4,
        size: 13,
        bold: true,
        color: { argb: '000000' },
      };
    }

    cell.alignment = {
      vertical: 'middle',
      horizontal: index === 0 ? 'center' : 'left',
      wrapText: true,
    };

    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'E7E6E6' },
    };

    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  sheet.getRow(headerRowIndex).height = 28;

  const sampleData = [
    [1, '141FBA10F5B4', 'Được phép sạc', '12F-345.67'],
    [2, 'A93C7D21E8F0', 'Được phép sạc', '99A-999.99'],
    [3, 'CF2B9C44AA71', 'Bị chặn', '30T-419.75'],
  ];

  sampleData.forEach((rowData, index) => {
    const row = sheet.getRow(headerRowIndex + 1 + index);

    row.values = rowData;

    row.eachCell((cell, colNumber) => {
      cell.font = {
        name: 'Times New Roman',
        size: 13,
      };

      cell.alignment = {
        vertical: 'middle',
        horizontal: colNumber === 1 ? 'center' : 'left',
      };

      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    row.height = 24;
  });

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob(
    [buffer],
    {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }
  );

  const url = window.URL.createObjectURL(blob);

  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = 'mau_danh_sach_ma_dinh_danh.xlsx';

  anchor.click();

  window.URL.revokeObjectURL(url);
};

  const handleSubmit = async () => {
    if (!selectedFile) {
      ToastHelper.showError('Vui lòng chọn tệp excel');
      return;
    }
    setUploading(true);
    try {
      const result = await ChargeHelper.API.Identifier.requestUploadIdentifierExcel(selectedPartnerId || null, selectedFile);

      if (result === true) {
        ToastHelper.showSuccess('Tải lên dữ liệu thành công');
        if (onDone) onDone();
      } else if (result?.invalid && result?.excelBase64) {
        ChargeHelper.UI.Excel.showDialogUploadIdentifierInvalidData(result.excelBase64);
      } else {
        ToastHelper.showError(_.isString(result) ? result : 'Tải lên thất bại');
      }
    } catch {
      ToastHelper.showError('Có lỗi xảy ra, vui lòng thử lại');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="px-4 pt-4 pb-2">
        {/* operator dropdown */}
        <div className="mb-4">
          {UIHelper.buildFormLabel('Đối tác sạc')}
          <KTBSDropdown
            name="vehicleIdentityPartnerId_upload"
            toggleElement={
              <button
                className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                type="button"
              >
                {selectedPartnerName ?? 'Chọn đối tác sạc'}
              </button>
            }
            selectedValue={selectedPartnerId}
            dropdownMenuItems={[
              { name: 'Chọn đối tác sạc', value: '' },
              ...(partnerList ?? []).map((partner) => ({
                name: partner.name,
                value: partner.vehicleIdentityPartnerId?.toString(),
              })),
            ]}
            dropdownMenuClassName="overflow-auto w-100"
            dropdownMenuStyle={{ maxHeight: '30vh' }}
            onChange={(value) => setSelectedPartnerId(value)}
            isDropdownForm={true}
          />
        </div>

        {/* template link */}
        <p style={{ fontSize: 13, color: AppColors.gray[600], marginBottom: 12 }}>
          Các dữ liệu trong tệp excel phải được chuẩn hóa theo mẫu sau:{' '}
          <span
            className="cursor-pointer"
            style={{ color: AppColors.primary, fontWeight: 600, textDecoration: 'underline' }}
            onClick={handleDownloadTemplate}
          >
            Tải tệp excel mẫu
          </span>
        </p>

        {/* upload zone label */}
        {UIHelper.buildFormLabel('Tải lên tệp excel đã được chuẩn hóa để nhập dữ liệu')}

        {/* drop zone */}
        <div
          className="mb-3"
          style={{
            border: `1px dashed ${dragging ? AppColors.primary : '#30BD6F'}`,
            borderRadius: 8,
            background: 'rgba(48, 189, 111, 0.05)',
            transition: 'all 0.2s',
          }}
        >
          <div
            className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
            style={{ padding: '32px 16px' }}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            {uploading ? (
              <>
                <div className="spinner-border text-success mb-2" style={{ width: 32, height: 32 }} />
                <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[500])}>
                  Dữ liệu đang được tải lên
                </span>
              </>
            ) : (
              <>
                <img src={imgFileArrowUp} alt="" style={{ width: 48, height: 48, objectFit: 'contain', marginBottom: 8 }} />
                <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                  Kéo thả hoặc ấn để tải lên
                </span>
                <span style={UIHelper.buildFontStyle('400', '12px', AppColors.gray[400])}>
                  Định dạng: .xls, .xlsx, .xlsm
                </span>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx,.xlsm"
            hidden
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
          />
        </div>

        {/* selected file */}
        {selectedFile && (
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2"
            style={{ background: AppColors.gray[50], borderRadius: 6, border: `1px solid ${AppColors.gray[200]}` }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="fa-solid fa-file-excel" style={{ color: '#1D6F42', fontSize: 16 }} />
              <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[700])}>
                {selectedFile.name}
              </span>
            </div>
            <button
              onClick={() => setSelectedFile(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: AppColors.gray[400], fontSize: 14 }}
            >
              <i className="fa-regular fa-xmark" />
            </button>
          </div>
        )}
      </div>

      <div
        className="d-flex align-items-center gap-3 px-4 py-3"
        style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null, true)}
        {UIHelper.buildGreenButton('Tải lên', handleSubmit, null, true, uploading)}
      </div>
    </div>
  );
}

export default DialogUploadIdentifierView;
